import { useMemo, useState, useEffect } from "react";
import { useAuth0V2 } from "@stadium/services/auth0";

import { FeatureFlagProvider } from "@stadium/services/launch-darkly";

import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import {
  getIsFetchingConfig,
  getLdAnonymousSecureModeHash,
} from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { LAUNCH_DARKYLY_CLIENT_ID } from "@constants/app-defaults";
import { LAUNCH_DARKLY_ANONYMOUS_USER_CONFIG } from "@stadium/services/launch-darkly/constants";
import { getClientIpFromGeoCookie } from "@stadium/helpers/cookie";

const LaunchDarklyWrapper = ({ children, clientIp: clientIpProp }) => {
  const [clientIpFromCookie, setClientIpFromCookie] = useState(null);
  useEffect(() => {
    setClientIpFromCookie(getClientIpFromGeoCookie());
  }, []);
  const clientIp = clientIpProp ?? clientIpFromCookie;
  const { isLoading, isAuthenticated } = useAuth0V2();
  const { currentUserData, ldData } = useCurrentUserContext();

  const { name, email } = currentUserData;

  const { userKey: key, secureModeHash: hash } = ldData;

  const ldAnonymousSecureModeHash = useConfig(getLdAnonymousSecureModeHash);
  const isFetchingConfig = useConfig(getIsFetchingConfig);

  const isReadyForInit = !isFetchingConfig && !isLoading && (isAuthenticated ? !!email : true);

  const configurations = useMemo(() => {
    if (!isReadyForInit) return {};

    const baseContext = key
      ? { key, name, email, kind: "user" }
      : LAUNCH_DARKLY_ANONYMOUS_USER_CONFIG;
    const context = clientIp ? { ...baseContext, ip: clientIp } : baseContext;

    return {
      context,
      options: { hash: hash || ldAnonymousSecureModeHash, disableSyncEventPost: true },
    };
  }, [isReadyForInit, key, name, email, hash, ldAnonymousSecureModeHash, clientIp]);

  return (
    <FeatureFlagProvider
      isReadyForInit={isReadyForInit}
      clientSideID={LAUNCH_DARKYLY_CLIENT_ID}
      configurations={configurations}
    >
      {children}
    </FeatureFlagProvider>
  );
};

export default LaunchDarklyWrapper;
